import { useState } from 'react';

import { Badge, Button, Group, Loader, Stack, Text } from '@mantine/core';

import { StatusBadge } from '@/components/StatusBadge';
import { useRegistryOrderDetail } from '@/hooks/useRegistryOrderDetail';
import {
  describeRegistryPrice,
  describeRegistryQuote,
  effectiveRegistryOrderStatus,
  hasRegistryOrderReply,
  mayHaveRegistryDocuments,
  REGISTRY_ORDER_STATUS_META,
  type RegistryOrder,
} from '@/types/registry-documents';
import { RegistryOrderDocumentList } from '../RegistryOrdersCard/RegistryOrderDocuments';
import classes from './RegistryConversation.module.css';

/**
 * The orders a report sent to Armadillo, one line each: what the research
 * desk knows the order by (its reference), the company, where it stands,
 * how many of the desk's emails on it wait for an answer, and its price or
 * the quote the desk gave. A prepared order has not left yet, so it is not
 * one of the desk's orders.
 */
export function ConversationOrders({ orders }: Readonly<{ orders: RegistryOrder[] }>) {
  const sent = orders.filter((order) => order.status !== 'prepared');
  if (sent.length === 0) {
    return null;
  }
  return (
    <Stack gap={4} role="list" aria-label="Orders sent to Armadillo" className={classes.orders}>
      {sent.map((order) => (
        <OrderChip key={order.id} order={order} />
      ))}
    </Stack>
  );
}

function OrderChip({ order }: Readonly<{ order: RegistryOrder }>) {
  const [filesOpen, setFilesOpen] = useState(false);
  // What arrived decides what is shown: the worker may not be allowed to
  // move the status itself (shadow mode).
  const meta = REGISTRY_ORDER_STATUS_META[effectiveRegistryOrderStatus(order)];
  // A quote the desk gave, once recorded, stands where the list price would.
  const price =
    describeRegistryQuote(order.quote) ??
    describeRegistryPrice(order.listPrice, order.currency, order.requiresQuote);

  return (
    <div
      role="listitem"
      className={classes.order}
      data-testid={`registry-conversation-order-${order.id}`}
    >
      <Group gap={6} wrap="wrap">
        <Text size="xs" ff="monospace" fw={600}>
          {order.reference}
        </Text>
        <Text size="xs">{order.companyName}</Text>
        <StatusBadge label={meta.label} color={meta.color} size="xs" />
        {hasRegistryOrderReply(order) && (
          <Badge size="xs" variant="light" color="orange">
            {order.replyCount} unanswered
          </Badge>
        )}
        {price && (
          <Text size="xs" c="dimmed">
            {price}
          </Text>
        )}
        {mayHaveRegistryDocuments(order.status) && (
          <OrderFilesButton
            order={order}
            open={filesOpen}
            onToggle={() => setFilesOpen((open) => !open)}
            ml="auto"
            data-testid={`registry-conversation-files-toggle-${order.id}`}
          />
        )}
      </Group>
      {filesOpen && (
        <OrderFiles order={order} data-testid={`registry-conversation-files-${order.id}`} />
      )}
    </div>
  );
}

/** "Files (2)": opens the files delivered against the order. Shared by the
 * order's chip and the thread's delivery lines. */
export function OrderFilesButton({
  order,
  open,
  onToggle,
  ml,
  'data-testid': testId,
}: Readonly<{
  order: Pick<RegistryOrder, 'receivedDocumentCount'>;
  open: boolean;
  onToggle: () => void;
  ml?: 'auto';
  'data-testid'?: string;
}>) {
  return (
    <Button
      size="compact-xs"
      variant="subtle"
      ml={ml}
      onClick={onToggle}
      aria-expanded={open}
      data-testid={testId}
    >
      Files ({order.receivedDocumentCount})
    </Button>
  );
}

/** The files delivered against one order, read only once the section is
 * opened: their download links are presigned and short-lived, so the
 * conversation itself never carries them. */
export function OrderFiles({
  order,
  'data-testid': testId,
}: Readonly<{ order: RegistryOrder; 'data-testid'?: string }>) {
  const { detail, isLoading, error, refetch } = useRegistryOrderDetail(
    order.id,
    order.updatedAt ?? 0
  );
  if (error && !detail) {
    return (
      <Group gap="xs" className={classes.files}>
        <Text size="xs" c="red">
          Could not load the files: {error.message}
        </Text>
        <Button size="compact-xs" variant="subtle" onClick={() => void refetch()}>
          Try again
        </Button>
      </Group>
    );
  }
  if (isLoading || !detail) {
    return (
      <Group gap="xs" className={classes.files}>
        <Loader size="xs" />
        <Text size="xs" c="dimmed">
          Loading files…
        </Text>
      </Group>
    );
  }
  return (
    <div className={classes.files} data-testid={testId}>
      <RegistryOrderDocumentList documents={detail.documents} />
    </div>
  );
}
